import { useId, type ReactNode } from "react";
import styles from "./screen.module.css";

/**
 * The three pieces every screen is built from, in the Living system design
 * (docs/design/living-system.md):
 *
 * - Lede: the sentence that says what matters now, written from the data, before any widget.
 * - Canvas: the screen's one drawn thing (a map, a pipeline, a chart), deep navy at night and
 *   white by day.
 * - Region: everything else, set on hairlines rather than boxed in cards.
 */

export function Lede({
  title,
  line,
  actions,
  className,
}: {
  /** A full sentence from the data, such as "12 cases open. Two are waiting for you." */
  title: ReactNode;
  /** One line under it: what the numbers mean, or what to do next. */
  line?: ReactNode;
  /** Controls at the right end, such as a search field or a period switch. */
  actions?: ReactNode;
  className?: string;
}) {
  return (
    <header className={`${styles.lede} ${className ?? ""}`}>
      <div className={styles.ledeText}>
        <h2 className={`zh-display ${styles.ledeTitle}`}>{title}</h2>
        {line ? <p className={styles.ledeLine}>{line}</p> : null}
      </div>
      {actions ? <div className={styles.ledeActions}>{actions}</div> : null}
    </header>
  );
}

export function Canvas({
  title,
  aside,
  children,
  className,
  bodyClassName,
  label,
  flush = false,
  id: sectionId,
}: {
  /** An anchor for links such as #handoffs. */
  id?: string;
  title?: ReactNode;
  /** A legend, a live chip or a link, at the right of the title. */
  aside?: ReactNode;
  children: ReactNode;
  className?: string;
  bodyClassName?: string;
  /** The accessible name when there is no visible title. */
  label?: string;
  /** No inner padding, for drawings that run to the edges. */
  flush?: boolean;
}) {
  const id = useId();
  const hasHead = Boolean(title) || Boolean(aside);
  return (
    <section
      id={sectionId}
      className={`zh-night ${styles.canvas} ${className ?? ""}`}
      aria-labelledby={title ? id : undefined}
      aria-label={title ? undefined : label}
    >
      {hasHead ? (
        <div className={styles.canvasHead}>
          {title ? (
            <h3 id={id} className={styles.canvasTitle}>
              {title}
            </h3>
          ) : (
            <span />
          )}
          {aside ? <div className={styles.canvasAside}>{aside}</div> : null}
        </div>
      ) : null}
      <div className={`${flush ? styles.canvasFlush : styles.canvasBody} ${bodyClassName ?? ""}`}>
        {children}
      </div>
    </section>
  );
}

export function Region({
  title,
  aside,
  children,
  className,
  first = false,
  id: sectionId,
}: {
  /** An anchor for links such as #finops. */
  id?: string;
  title?: ReactNode;
  aside?: ReactNode;
  children: ReactNode;
  className?: string;
  /** The first region under the lede has no rule above it. */
  first?: boolean;
}) {
  const id = useId();
  return (
    <section
      id={sectionId}
      className={`${styles.region} ${first ? styles.regionFirst : ""} ${className ?? ""}`}
      aria-labelledby={title ? id : undefined}
    >
      {title || aside ? (
        <div className={styles.regionHead}>
          {title ? (
            <h3 id={id} className={styles.regionTitle}>
              {title}
            </h3>
          ) : (
            <span />
          )}
          {aside ? <div className={styles.regionAside}>{aside}</div> : null}
        </div>
      ) : null}
      {children}
    </section>
  );
}

/** A small round chip for live state: "Live", "3 open", "Healed". Colour is state. */
export function StateChip({
  tone,
  children,
  pulse = false,
}: {
  tone: "crew" | "ok" | "person" | "broken" | "model" | "quiet";
  children: ReactNode;
  /** A beating dot, only while something is actually happening. */
  pulse?: boolean;
}) {
  return (
    <span className={`${styles.chip} ${styles[`chip_${tone}`]}`}>
      <span className={`${styles.chipDot} ${pulse ? styles.chipPulse : ""}`} aria-hidden="true" />
      {children}
    </span>
  );
}
